import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import { FaUserCheck, FaFileInvoiceDollar, FaCalendarAlt, FaChevronRight, FaChevronLeft, FaSpinner, FaCheckCircle, FaPrint, FaDollarSign, FaGift, FaPencilAlt } from 'react-icons/fa';
// <<< CORREÇÃO AQUI: Importar 'parse' além de 'format' >>>
import { format, parse } from 'date-fns';
import './AcertoMotoristas.css'; // O CSS "transformador"

const formatCurrency = (value) => {
    const number = parseFloat(value);
    return isNaN(number) ? 'R$ 0,00' : number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// <<< CORREÇÃO AQUI: Usar 'parse' para interpretar a data corretamente >>>
const formatDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') return ''; // Verifica se é string válida
    try {
        // 1. Interpreta a string 'yyyy-MM-dd' como data local
        const parsedDate = parse(dateString, 'yyyy-MM-dd', new Date());
        // 2. Formata a data interpretada para 'dd/MM/yyyy'
        return format(parsedDate, 'dd/MM/yyyy');
    } catch (e) {
        console.error("Erro ao formatar data:", dateString, e);
        return 'Inválida';
    }
};


export default function AcertoMotoristas() {
    const [step, setStep] = useState(1);
    const [motoristas, setMotoristas] = useState([]);
    const [selectedMotoristaId, setSelectedMotoristaId] = useState('');
    const [dateRange, setDateRange] = useState({
        inicio: new Date(new Date().setDate(1)).toISOString().split('T')[0],
        fim: new Date().toISOString().split('T')[0]
    });
    const [isGenerating, setIsGenerating] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    // Step 2 Data
    const [extratoData, setExtratoData] = useState(null); // Renomeado para evitar conflito
    const [motoristaDetalhes, setMotoristaDetalhes] = useState(null);
    const [manualCommission, setManualCommission] = useState(''); // Estado para comissão manual
    const [showManualCommissionInput, setShowManualCommissionInput] = useState(false); // Controla visibilidade
    const [calculatedCommission, setCalculatedCommission] = useState(0); // Para guardar a comissão calculada

    const fetchMotoristas = useCallback(async () => {
        try {
            const response = await api.get('/api/drivers', { params: { limit: 1000, page: 1 } });
            setMotoristas(response.data.data);
        } catch (err) {
            console.error("Erro ao buscar motoristas:", err);
            toast.error("Não foi possível carregar a lista de motoristas.");
        }
    }, []);

    useEffect(() => {
        fetchMotoristas();
    }, [fetchMotoristas]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'motorista_id') {
            setSelectedMotoristaId(value);
        } else {
            setDateRange(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleGerarExtrato = async (e) => {
        e.preventDefault();
        if (!selectedMotoristaId) {
            toast.warn("Por favor, selecione um motorista.");
            return;
        }
        setIsGenerating(true);
        setManualCommission(''); // Limpa comissão manual ao gerar novo extrato
        setShowManualCommissionInput(false); // Esconde input
        try {
            const response = await api.get('/api/acerto/gerar-extrato', {
                params: {
                    motorista_id: selectedMotoristaId,
                    data_inicio: dateRange.inicio,
                    data_fim: dateRange.fim
                }
            });

            setExtratoData(response.data);
            setMotoristaDetalhes(motoristas.find(m => m.id === parseInt(selectedMotoristaId)));
            // Guarda a comissão calculada (Créditos - Bônus, se houver)
            const bonus = response.data.extrato.find(item => item.descricao?.includes('Bônus'))?.credito || 0;
            setCalculatedCommission(response.data.totalCredito - bonus);

            setStep(2); // Avança para a etapa de revisão

        } catch (err) {
            console.error("Erro ao gerar extrato:", err);
            toast.error(err.response?.data?.error || "Falha ao gerar extrato.");
        } finally {
            setIsGenerating(false);
        }
    };

    // Recalcula o valor líquido quando a comissão manual muda
    const finalNetValue = useCallback(() => {
        if (!extratoData) return 0;

        const manualCommissionValue = parseFloat(manualCommission);
        const hasManualCommission = !isNaN(manualCommissionValue);

        const baseCredits = hasManualCommission ? manualCommissionValue : calculatedCommission;
        const bonus = extratoData.extrato.find(item => item.descricao?.includes('Bônus'))?.credito || 0;
        const totalCredits = baseCredits + bonus;
        const totalDebits = extratoData.totalDebito;

        return totalCredits - totalDebits;
    }, [extratoData, manualCommission, calculatedCommission]);


    const handleFecharAcerto = async () => {
        if (!extratoData) return;

        const valorLiquidoFinal = finalNetValue();
        const valorComissaoManualUsado = showManualCommissionInput && manualCommission !== '' ? parseFloat(manualCommission) : null;


        const confirmMessage = `Confirma o fechamento do acerto no valor líquido de ${formatCurrency(valorLiquidoFinal)} para ${motoristaDetalhes.nome}?` +
            (valorComissaoManualUsado !== null ? `\n(Usando comissão manual de ${formatCurrency(valorComissaoManualUsado)})` : '') +
            "\nEsta ação irá lançar uma despesa no financeiro.";

        const confirm = window.confirm(confirmMessage);

        if (!confirm) return;

        setIsClosing(true);
        try {
            await api.post('/api/acerto/fechar-acerto', {
                motorista_id: selectedMotoristaId,
                valor_liquido_final: valorLiquidoFinal, // Envia o valor final recalculado
                extrato_json: extratoData.extrato,
                data_inicio: extratoData.data_inicio_usada, // Usa as datas retornadas pela API
                data_fim: extratoData.data_fim_usada,     // Usa as datas retornadas pela API
                valor_comissao_manual: valorComissaoManualUsado // Envia o valor manual (ou null)
            });

            toast.success("Acerto fechado e lançado no financeiro com sucesso!");
            handleVoltar(); // Volta para a etapa 1

        } catch (err) {
            console.error("Erro ao fechar acerto:", err);
            toast.error(err.response?.data?.error || "Falha ao fechar acerto.");
        } finally {
            setIsClosing(false);
        }
    };

    // Voltar para Etapa 1
    const handleVoltar = () => {
        setStep(1);
        setExtratoData(null);
        setMotoristaDetalhes(null);
        setManualCommission('');
        setShowManualCommissionInput(false);
        setCalculatedCommission(0);
        // Mantém o motorista e data selecionados para facilitar
    };


    // Calcula o bônus separadamente para exibição
    const bonusInfo = extratoData?.extrato.find(item => item.descricao?.includes('Bônus'));

    return (
        <div className="acerto-page-container">
            <h1 className="h3 text-gray-800 acerto-main-title"><FaFileInvoiceDollar /> Acerto de Motoristas</h1>

            <div className="acerto-container">
                {/* --- ETAPA 1: SELEÇÃO --- */}
                {step === 1 && (
                    <div className="acerto-card">
                        <h3 className="acerto-card-title"><FaUserCheck /> Etapa 1: Gerar Extrato</h3>
                        <p className="acerto-card-subtitle">Selecione o motorista e o período para calcular as comissões e débitos.</p>

                        <form onSubmit={handleGerarExtrato}>
                             {/* ... (campos Motorista e Datas - sem alterações) ... */}
                            <div className="acerto-form-group">
                                <label>Motorista</label>
                                <select name="motorista_id" value={selectedMotoristaId} onChange={handleInputChange} required>
                                    <option value="">Selecione um motorista...</option>
                                    {motoristas.map(m => (
                                        <option key={m.id} value={m.id}>{m.nome}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="date-range-group">
                                <div className="acerto-form-group">
                                    <label><FaCalendarAlt /> Data de Início</label>
                                    <input type="date" name="inicio" value={dateRange.inicio} onChange={handleInputChange} required />
                                </div>
                                <div className="acerto-form-group">
                                    <label><FaCalendarAlt /> Data de Fim</label>
                                    <input type="date" name="fim" value={dateRange.fim} onChange={handleInputChange} required />
                                </div>
                            </div>

                            <div className="acerto-actions">
                                <button type="submit" className="btn-acerto-primary" disabled={isGenerating}>
                                    {isGenerating ? <FaSpinner className="spin" /> : <FaChevronRight />}
                                    {isGenerating ? 'Gerando...' : 'Gerar Extrato'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* --- ETAPA 2: REVISÃO E FECHAMENTO --- */}
                {step === 2 && extratoData && (
                    <div className="acerto-card review-step">
                        <div className="acerto-header">
                            <button onClick={handleVoltar} className="btn-acerto-back">
                                <FaChevronLeft /> Voltar
                            </button>
                            <h3 className="acerto-card-title">Etapa 2: Revisar e Fechar Acerto</h3>
                            <button onClick={() => window.print()} className="btn-acerto-print">
                                <FaPrint /> Imprimir
                            </button>
                        </div>

                        {/* <<< CORREÇÃO DATA: Usa as datas retornadas pela API >>> */}
                        <div className="acerto-motorista-info">
                            <strong>Motorista:</strong> {motoristaDetalhes?.nome} <br/>
                            <strong>Período:</strong> {formatDate(extratoData.data_inicio_usada)} até {formatDate(extratoData.data_fim_usada)} <br/>
                            <strong>Regra Aplicada:</strong> {extratoData.configUsada.tipo} ({extratoData.configUsada.valor}%)
                        </div>

                        {/* Resumo Financeiro */}
                        <div className="extrato-summary">
                            <div className="summary-box total-credito">
                                <label>Total Créditos</label>
                                {/* Exibe comissão base ou manual + bônus */}
                                <span>
                                     {formatCurrency((showManualCommissionInput && manualCommission !== '' ? parseFloat(manualCommission) : calculatedCommission) + (bonusInfo?.credito || 0))}
                                </span>
                                {/* Botão para editar comissão */}
                                <button onClick={() => setShowManualCommissionInput(!showManualCommissionInput)} className="edit-commission-btn">
                                    <FaPencilAlt /> {showManualCommissionInput ? 'Usar Calculada' : 'Informar Manual'}
                                </button>
                                {/* Campo de comissão manual */}
                                {showManualCommissionInput && (
                                    <div className="manual-commission-input">
                                        <input
                                            type="number"
                                            step="0.01"
                                            placeholder={`Calculado: ${formatCurrency(calculatedCommission)}`}
                                            value={manualCommission}
                                            onChange={(e) => setManualCommission(e.target.value)}
                                        />
                                         <small>Valor base da comissão (sem bônus)</small>
                                    </div>
                                )}
                                {/* Exibe detalhes do bônus se aplicável */}
                                {bonusInfo && (
                                    <small className="bonus-info">
                                        <FaGift /> {bonusInfo.descricao} (+{formatCurrency(bonusInfo.credito)})
                                    </small>
                                )}
                            </div>
                            <div className="summary-box total-debito">
                                <label>Total Débitos (Adiant/Multas)</label>
                                <span>{formatCurrency(extratoData.totalDebito)}</span>
                            </div>
                            <div className="summary-box valor-liquido">
                                <label>Valor Líquido a Pagar</label>
                                <span>{formatCurrency(finalNetValue())}</span>
                            </div>
                        </div>

                        {/* Tabela de Extrato Detalhado */}
                        <h4 className="extrato-table-title"><FaDollarSign /> Extrato Detalhado</h4>
                        <div className="extrato-table-container">
                            <table className="extrato-table">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Histórico</th>
                                        <th className="col-debito">Débito</th>
                                        <th className="col-credito">Crédito</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {extratoData.extrato.length === 0 && (
                                        <tr>
                                            <td colSpan="4" style={{textAlign: 'center', padding: '20px'}}>Nenhum lançamento encontrado para este período.</td>
                                        </tr>
                                    )}
                                    {extratoData.extrato.map((item, index) => (
                                        <tr key={index}>
                                            {/* <<< CORREÇÃO DATA: Usa formatDate aqui também >>> */}
                                            <td>{formatDate(item.data)}</td>
                                            <td>{item.descricao}</td>
                                            <td className="col-debito">{item.debito > 0 ? formatCurrency(item.debito) : '-'}</td>
                                            <td className="col-credito">{item.credito > 0 ? formatCurrency(item.credito) : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="acerto-actions">
                            <button onClick={handleFecharAcerto} className="btn-acerto-confirm" disabled={isClosing}>
                                {isClosing ? <FaSpinner className="spin" /> : <FaCheckCircle />}
                                {isClosing ? 'Fechando...' : 'Confirmar e Fechar Acerto'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}